"use client";

import { Box, Container, Typography, keyframes, useMediaQuery, useTheme } from "@mui/material";
import { useMemo } from "react";
import SalifyLink from "src/lib/components/link/salify-link";
import { toAssetImages } from "src/lib/utils";
import {
  LayoutViewMode,
  ResponsivePropsConfig,
  resolveResponsiveProps,
  splitDesktopAndResponsive,
} from "src/lib/utils/responsive-props";

export type MarqueeLogoItem = {
  image: string;
  link?: string;
};

type MarqueeViewportData = {
  backgroundImage?: string;
  backgroundColor?: string;
  description?: string;
  title?: string;
  logos?: MarqueeLogoItem[];
};

export interface VariantProps extends MarqueeViewportData {
  responsive?: ResponsivePropsConfig<MarqueeViewportData>;
}

const ACCENT = "#FFA411";
const DEFAULT_BG = "#f3f3f3";
const CARD_BORDER = "#e8e8e8";

const marqueeScroll = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
`;

const useLayoutViewMode = (): LayoutViewMode => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("tablet"));
  const isTablet = useMediaQuery(theme.breakpoints.between("tablet", "laptop"));
  if (isMobile) return "mobile";
  if (isTablet) return "tablet";
  return "desktop";
};

const LogoCard = ({ item, index }: { item: MarqueeLogoItem; index: number }) => {
  const imageUrl = item.image?.trim() ? toAssetImages(item.image as string) : "";
  const href = item.link?.trim() || "";

  const card = (
    <Box
      sx={{
        flex: "0 0 auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: { xs: 140, laptop: 200 },
        height: { xs: 80, laptop: 100 },
        px: { xs: 2.5, laptop: 4 },
        py: { xs: 1.75, laptop: 3 },
        borderRadius: 1.5,
        border: `1px solid ${CARD_BORDER}`,
        bgcolor: "#fff",
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        transition: "border-color 0.25s, box-shadow 0.25s",
        "&:hover": href
          ? {
              borderColor: ACCENT,
              boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            }
          : undefined,
      }}
    >
      {imageUrl ? (
        <Box
          component="img"
          src={imageUrl}
          alt={`Logo ${index + 1}`}
          loading="lazy"
          sx={{
            maxWidth: { xs: 120, laptop: 180 },
            maxHeight: { xs: 48, laptop: 64 },
            width: "auto",
            height: "auto",
            objectFit: "contain",
          }}
        />
      ) : (
        <Box
          sx={{
            width: { xs: 80, laptop: 120 },
            height: { xs: 32, laptop: 40 },
            bgcolor: "grey.100",
            borderRadius: 0.5,
          }}
        />
      )}
    </Box>
  );

  if (href) {
    return (
      <SalifyLink href={href}>
        <Box sx={{ display: "flex", flexShrink: 0 }}>{card}</Box>
      </SalifyLink>
    );
  }

  return card;
};

const MarqueeDefaultV2 = (props: VariantProps) => {
  const viewMode = useLayoutViewMode();
  const { desktop, responsive } = splitDesktopAndResponsive(
    props as unknown as Record<string, unknown>,
  );

  const data = useMemo((): MarqueeViewportData => {
    const resolved = resolveResponsiveProps(
      {
        backgroundImage: (desktop.backgroundImage as string) || "",
        backgroundColor: (desktop.backgroundColor as string) || DEFAULT_BG,
        description: (desktop.description as string) || "",
        title: (desktop.title as string) || "",
        logos: (desktop.logos as MarqueeLogoItem[]) || [],
      },
      responsive,
      viewMode,
      ["backgroundImage", "backgroundColor", "description", "title", "logos"],
    );

    return {
      backgroundImage: String(resolved.backgroundImage || ""),
      backgroundColor: String(resolved.backgroundColor || DEFAULT_BG),
      description: String(resolved.description || ""),
      title: String(resolved.title || ""),
      logos: Array.isArray(resolved.logos) ? resolved.logos : [],
    };
  }, [desktop, responsive, viewMode]);

  const backgroundUrl = data.backgroundImage?.trim()
    ? toAssetImages(data.backgroundImage as string)
    : "";
  const logos = (data.logos || []).filter((item) => item?.image?.trim());
  const sectionBg = data.backgroundColor || DEFAULT_BG;
  const trackItems = logos.length > 0 ? [...logos, ...logos] : [];

  return (
    <Box
      component="section"
      id="marquee"
      aria-labelledby="marquee-heading"
      sx={{
        position: "relative",
        py: { xs: 6, laptop: 8 },
        bgcolor: sectionBg,
        backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        overflow: "hidden",
      }}
    >
      {backgroundUrl ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(255,255,255,0.9)",
          }}
        />
      ) : null}

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 1.5,
            mb: { xs: 4, laptop: 6 },
            px: { xs: 1, laptop: 0 },
          }}
        >
          {data.description ? (
            <Typography
              component="p"
              sx={{
                color: ACCENT,
                fontSize: { xs: 14, laptop: 15 },
                fontWeight: 600,
              }}
            >
              {data.description}
            </Typography>
          ) : null}

          {data.title ? (
            <Typography
              component="h2"
              id="marquee-heading"
              sx={{
                fontWeight: 800,
                fontSize: { xs: 26, tablet: 32, laptop: 40 },
                lineHeight: 1.25,
                color: "#1a2b4a",
                letterSpacing: "-0.02em",
                maxWidth: 900,
              }}
            >
              {data.title}
            </Typography>
          ) : null}
        </Box>
      </Container>

      {trackItems.length > 0 ? (
        <Box sx={{ position: "relative", width: "100%" }}>
          <Box
            aria-hidden
            sx={{
              pointerEvents: "none",
              position: "absolute",
              inset: "0 auto 0 0",
              zIndex: 2,
              width: { xs: 40, laptop: 64 },
              background: `linear-gradient(to right, ${sectionBg}, transparent)`,
            }}
          />
          <Box
            aria-hidden
            sx={{
              pointerEvents: "none",
              position: "absolute",
              inset: "0 0 0 auto",
              zIndex: 2,
              width: { xs: 40, laptop: 64 },
              background: `linear-gradient(to left, ${sectionBg}, transparent)`,
            }}
          />

          <Box
            sx={{
              display: "flex",
              width: "max-content",
              alignItems: "center",
              gap: { xs: 2.5, laptop: 4 },
              animation: `${marqueeScroll} ${viewMode === "mobile" ? "25s" : "40s"} linear infinite`,
              "&:hover": {
                animationPlayState: "paused",
              },
            }}
          >
            {trackItems.map((item, index) => (
              <LogoCard key={`${item.image}-${index}`} item={item} index={index % logos.length} />
            ))}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default MarqueeDefaultV2;
